import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';

export default function ManufacturerProfile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [logo, setLogo] = useState(null);
  const [documents, setDocuments] = useState([]);

  const [formData, setFormData] = useState({
    businessName: '',
    description: '',
    contact: {
      email: user?.email || '',
      phone: '',
      address: {
        street: '',
        city: '',
        state: '',
        pincode: ''
      }
    },
    serviceArea: {
      pincodes: [],
      pincodesInput: '', // Temporary field for comma-separated input
      radius: ''
    },
    capabilities: {
      printMethods: [],
      maxOrderSize: 0,
      turnaroundTime: 0
    },
    businessInfo: {
      gstNumber: '',
      panNumber: ''
    }
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const { data } = await api.get('/manufacturers/me');
        if (data.data.manufacturer) {
          setProfile(data.data.manufacturer);
          setFormData({
            businessName: data.data.manufacturer.businessName || '',
            description: data.data.manufacturer.description || '',
            contact: data.data.manufacturer.contact || {
              email: user?.email || '',
              phone: '',
              address: {
                street: '',
                city: '',
                state: '',
                pincode: ''
              }
            },
            serviceArea: {
              ...data.data.manufacturer.serviceArea,
              pincodesInput: data.data.manufacturer.serviceArea?.pincodes?.join(', ') || ''
            },
            capabilities: data.data.manufacturer.capabilities || {
              printMethods: [],
              maxOrderSize: 0,
              turnaroundTime: 0
            },
            businessInfo: data.data.manufacturer.businessInfo || {
              gstNumber: '',
              panNumber: ''
            }
          });
        } else {
          // Initialize empty form if no profile exists
          setFormData({
            businessName: '',
            description: '',
            contact: {
              email: user?.email || '',
              phone: '',
              address: {
                street: '',
                city: '',
                state: '',
                pincode: ''
              }
            },
            serviceArea: {
              pincodes: [],
              pincodesInput: '',
              radius: ''
            },
            capabilities: {
              printMethods: [],
              maxOrderSize: 0,
              turnaroundTime: 0
            },
            businessInfo: {
              gstNumber: '',
              panNumber: ''
            }
          });
        }
      } catch (err) {
        if (err.response?.status === 404) {
          // No profile exists yet, initialize empty form
          setFormData({
            businessName: '',
            description: '',
            contact: {
              email: user?.email || '',
              phone: '',
              address: {
                street: '',
                city: '',
                state: '',
                pincode: ''
              }
            },
            serviceArea: {
              pincodes: [],
              pincodesInput: '',
              radius: ''
            },
            capabilities: {
              printMethods: [],
              maxOrderSize: 0,
              turnaroundTime: 0
            },
            businessInfo: {
              gstNumber: '',
              panNumber: ''
            }
          });
          setIsEditing(true);
        } else {
          console.error('Error fetching profile:', err);
          toast.error('Failed to load profile');
        }
      } finally {
        setLoading(false);
      }
    };

    if (user) fetchProfile();
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle nested objects (like contact.address)
    if (name.includes('.')) {
      const keys = name.split('.');
      setFormData(prev => ({
        ...prev,
        [keys[0]]: {
          ...prev[keys[0]],
          [keys[1]]: keys.length > 2 ? {
            ...prev[keys[0]][keys[1]],
            [keys[2]]: value
          } : value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleArrayChange = (field, value, checked) => {
    setFormData(prev => ({
      ...prev,
      capabilities: {
        ...prev.capabilities,
        [field]: checked 
          ? [...(prev.capabilities[field] || []), value]
          : (prev.capabilities[field] || []).filter(item => item !== value)
      }
    }));
  };

  const handlePincodesChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      serviceArea: {
        ...prev.serviceArea,
        pincodesInput: value
      }
    }));
  };

  const handlePincodesBlur = (e) => {
    const pincodes = e.target.value.split(',').map(p => p.trim()).filter(p => p);
    setFormData(prev => ({
      ...prev,
      serviceArea: {
        ...prev.serviceArea,
        pincodes,
        pincodesInput: undefined
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formDataToSend = new FormData();
      
      // Prepare the final pincodes data
      const finalPincodes = formData.serviceArea.pincodesInput 
        ? formData.serviceArea.pincodesInput.split(',').map(p => p.trim()).filter(p => p)
        : formData.serviceArea.pincodes;

      // Append all fields
      formDataToSend.append('businessName', formData.businessName);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('contact', JSON.stringify(formData.contact));
      formDataToSend.append('serviceArea', JSON.stringify({
        ...formData.serviceArea,
        pincodes: finalPincodes
      }));
      formDataToSend.append('capabilities', JSON.stringify(formData.capabilities));
      formDataToSend.append('businessInfo', JSON.stringify(formData.businessInfo));

      if (logo) {
        formDataToSend.append('logo', logo);
      }

      if (documents.length > 0) {
        documents.forEach(doc => {
          formDataToSend.append('verificationDocuments', doc);
        });
      }

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      };

      let response;
      if (profile) {
        response = await api.put(`/manufacturers/${profile._id}`, formDataToSend, config);
      } else {
        response = await api.post('/manufacturers', formDataToSend, config);
      }

      toast.success(profile ? 'Profile updated!' : 'Profile created!');
      setProfile(response.data.data.manufacturer);
      setIsEditing(false);
      setLogo(null);
      setDocuments([]);
    } catch (err) {
      console.error('Error saving profile:', err);
      toast.error(err.response?.data?.message || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-extrabold text-gray-900">
            {profile ? 'Your Manufacturer Profile' : 'Become a Manufacturer'}
          </h1>
          <p className="mt-2 text-lg text-gray-600">
            {profile ? 'Manage your manufacturing business' : 'Join our network of trusted manufacturers'}
          </p>
        </div>

        <div className="bg-white shadow-xl rounded-2xl overflow-hidden">
          {!isEditing && profile ? (
            <div className="p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row gap-6">
                <div className="flex-shrink-0">
                  {profile.logo ? (
                    <img 
                      src={profile.logo} 
                      alt={profile.businessName}
                      className="h-32 w-32 rounded-full object-cover border-4 border-white shadow-md"
                    />
                  ) : (
                    <div className="h-32 w-32 rounded-full bg-gray-200 flex items-center justify-center text-4xl">
                      🏭
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900">{profile.businessName}</h2>
                  <p className="text-gray-600 mt-2">{profile.description}</p>
                  
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Contact</h3>
                      <p className="text-gray-900">{profile.contact?.email}</p>
                      <p className="text-gray-900">{profile.contact?.phone}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Address</h3>
                      <p className="text-gray-900">{profile.contact?.address?.street}</p>
                      <p className="text-gray-900">
                        {profile.contact?.address?.city}, {profile.contact?.address?.state} {profile.contact?.address?.pincode}
                      </p>
                    </div>
                  </div>

                  <div className="mt-6">
                    <button
                      onClick={() => setIsEditing(true)}
                      className="px-6 py-2 bg-black text-white rounded-full hover:bg-gray-800 transition-all"
                    >
                      Edit Profile
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Capabilities</h3>
                <div className="mt-4 flex flex-wrap gap-2">
                  {(profile.capabilities?.printMethods || []).map(method => (
                    <span key={method} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                      {method}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-6 sm:p-8">
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Business Name</label>
                    <input
                      type="text"
                      name="businessName"
                      value={formData.businessName || ''}
                      onChange={handleChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Business Logo</label>
                    <div className="mt-1 flex items-center">
                      <div className="h-12 w-12 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                        {logo ? (
                          <img 
                            src={URL.createObjectURL(logo)} 
                            alt="Logo preview"
                            className="h-full w-full object-cover"
                          />
                        ) : profile?.logo ? (
                          <img 
                            src={profile.logo} 
                            alt="Current logo"
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <span className="text-gray-400">🏭</span>
                        )}
                      </div>
                      <label className="ml-4 cursor-pointer">
                        <span className="px-3 py-1 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50">
                          Change
                        </span>
                        <input 
                          type="file" 
                          className="sr-only"
                          onChange={(e) => setLogo(e.target.files[0])}
                          accept="image/*"
                        />
                      </label>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    name="description"
                    value={formData.description || ''}
                    onChange={handleChange}
                    rows={3}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                    required
                  />
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-medium text-gray-900">Contact Information</h3>
                  <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Email</label>
                      <input
                        type="email"
                        name="contact.email"
                        value={formData.contact?.email || ''}
                        onChange={handleChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Phone</label>
                      <input
                        type="tel"
                        name="contact.phone"
                        value={formData.contact?.phone || ''}
                        onChange={handleChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                        required
                      />
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Street Address</label>
                      <input
                        type="text"
                        name="contact.address.street"
                        value={formData.contact?.address?.street || ''}
                        onChange={handleChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">City</label>
                      <input
                        type="text"
                        name="contact.address.city"
                        value={formData.contact?.address?.city || ''}
                        onChange={handleChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                        required
                      />
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">State</label>
                      <input
                        type="text"
                        name="contact.address.state"
                        value={formData.contact?.address?.state || ''}
                        onChange={handleChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Pincode</label>
                      <input
                        type="text"
                        name="contact.address.pincode"
                        value={formData.contact?.address?.pincode || ''}
                        onChange={handleChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-medium text-gray-900">Service Area</h3>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700">Pincodes Covered (comma separated)</label>
                    <input
                      type="text"
                      value={formData.serviceArea?.pincodesInput || formData.serviceArea?.pincodes?.join(', ') || ''}
                      onChange={handlePincodesChange}
                      onBlur={handlePincodesBlur}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                      placeholder="560001, 560002, 560003"
                    />
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-medium text-gray-900">Capabilities</h3>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700">Print Methods</label>
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {['screen-print', 'dtg', 'embroidery', 'vinyl', 'heat-transfer'].map(method => (
                        <div key={method} className="flex items-center">
                          <input
                            id={`method-${method}`}
                            name="capabilities.printMethods"
                            type="checkbox"
                            checked={(formData.capabilities?.printMethods || []).includes(method)}
                            onChange={(e) => handleArrayChange('printMethods', method, e.target.checked)}
                            className="h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
                          />
                          <label htmlFor={`method-${method}`} className="ml-2 text-sm text-gray-700">
                            {method}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-medium text-gray-900">Business Information</h3>
                  <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">GST Number</label>
                      <input
                        type="text"
                        name="businessInfo.gstNumber"
                        value={formData.businessInfo?.gstNumber || ''}
                        onChange={handleChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">PAN Number</label>
                      <input
                        type="text"
                        name="businessInfo.panNumber"
                        value={formData.businessInfo?.panNumber || ''}
                        onChange={handleChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-medium text-gray-900">Verification Documents</h3>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Upload documents (GST, PAN, Business License)
                    </label>
                    <div className="mt-2 flex items-center">
                      <label className="cursor-pointer">
                        <span className="px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50">
                          Select Files
                        </span>
                        <input 
                          type="file" 
                          className="sr-only"
                          onChange={(e) => setDocuments([...documents, ...Array.from(e.target.files)])}
                          multiple
                        />
                      </label>
                      <span className="ml-2 text-sm text-gray-500">
                        {documents.length > 0 ? `${documents.length} files selected` : 'No files selected'}
                      </span>
                    </div>
                    {profile?.verificationDocuments?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">Previously uploaded:</p>
                        <ul className="list-disc pl-5 text-sm text-gray-500">
                          {profile.verificationDocuments.map((doc, index) => (
                            <li key={index}>{doc.type} - Uploaded</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  {profile && (
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditing(false);
                        setLogo(null);
                        setDocuments([]);
                      }}
                      className="px-6 py-2 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-pink-600 text-white rounded-full hover:bg-pink-700 disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : profile ? 'Update Profile' : 'Create Profile'}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}